import { useState } from 'react'
import { videoApi } from '@carrent/api'
import { Icon, JitsiRoom, PageHeader } from '@carrent/ui'
import {
  CALL_STATUSES,
  isJoinable,
  useVideoCalls,
  useVideoSettings,
} from '@carrent/domain'

/**
 * Звонки водителя.
 *
 * Одна колонка, вход прямо из списка — на телефоне промежуточная
 * карточка перед звонком лишняя: водителя позвали, он нажал, он внутри.
 * Пуш о приглашении ведёт сюда же (url в payload).
 *
 * Водитель не создаёт звонки с этого экрана, даже если владелец
 * разрешил drivers_can_create: его сценарий — кнопка «Видеоосмотр»
 * будет жить в контексте активной поездки (следующий шаг), а не
 * в пустом списке.
 */

export default function VideoCallsScreen() {
  const { settings } = useVideoSettings()
  const { items, loading, error, refresh } = useVideoCalls()
  const [active, setActive] = useState(null) // { call, token }
  const [joinError, setJoinError] = useState('')
  const [joiningId, setJoiningId] = useState(null)

  const join = async (call) => {
    setJoiningId(call.id)
    setJoinError('')
    try {
      const { data } = await videoApi.token(call.id)
      setActive({ call, token: data.data })
    } catch (e) {
      setJoinError(e.response?.data?.message || 'Не удалось войти в звонок')
    } finally {
      setJoiningId(null)
    }
  }

  if (active) {
    return (
      <div className="video-screen video-screen--room">
        <JitsiRoom
          token={active.token}
          subject={active.call.title}
          roomConfig={{
            chat_enabled: settings.chat_enabled,
            screenshare: settings.screenshare_enabled,
            start_muted_after: settings.start_muted_after,
          }}
          onClose={() => {
            videoApi.leave(active.call.id)
            setActive(null)
            refresh()
          }}
        />
      </div>
    )
  }

  return (
    <div className="video-screen">
      <PageHeader title="Видеозвонки" />

      {!settings.enabled && (
        <div className="video-screen__state">
          <Icon name="chat" size={40} />
          <p className="text-muted">Видеозвонки отключены администратором.</p>
        </div>
      )}

      {settings.enabled && loading && (
        <div className="video-screen__state">
          <span className="spinner" aria-label="Загрузка" />
        </div>
      )}

      {settings.enabled && !loading && error && (
        <div className="video-screen__state">
          <p className="text-muted">Не удалось загрузить звонки.</p>
          <button type="button" className="btn btn--secondary" onClick={refresh}>
            Повторить
          </button>
        </div>
      )}

      {settings.enabled && !loading && !error && items.length === 0 && (
        <div className="video-screen__state">
          <Icon name="chat" size={40} />
          <p className="text-muted">
            Пока пусто. Диспетчер пригласит вас — звонок появится здесь,
            а на телефон придёт уведомление.
          </p>
        </div>
      )}

      {joinError && <div className="alert alert--error">{joinError}</div>}

      {settings.enabled && items.length > 0 && (
        <ul className="video-screen__list">
          {items.map((call) => {
            const st = CALL_STATUSES[call.status] || { label: call.status }
            const joinable = isJoinable(call)
            return (
              <li key={call.id} className="video-screen__item">
                <div className="video-screen__item-body">
                  <span className="video-screen__item-title">{call.title}</span>
                  <span className="text-muted video-screen__item-sub">
                    {st.label}
                    {call.created_by ? ` · ${call.created_by.full_name}` : ''}
                  </span>
                </div>
                {joinable && (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => join(call)}
                    disabled={joiningId === call.id}
                  >
                    {joiningId === call.id
                      ? <span className="spinner spinner--sm" />
                      : 'Войти'}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
